import type { CartItem, OrderTotals } from "@/types/pos"
import type { ManualDiscount } from "@/hooks/useCart"
import { applyDiscountStack, type CouponMath } from "@/lib/coupons/engine"
import { snapshotHasScope } from "@/lib/coupons/scopeTripwire"
import { couponBaseFromLines, type CouponDiscountExclusions } from "@/lib/coupons/alcoholExclusion"
import { retailLineTotal } from "@/lib/retail/lineTotal"

// PHASE D — items.price is now the CARD (regular) price.
// Cash discount applies as an EXPLICIT discount when paymentMethod='cash'.
// Card pago: no surcharge; the customer pays items.price directly.
//
// SYSTEM NORM (2026-05-28): The merchant enters the price they want to RECEIVE
// (cash price). The system automatically computes the card price by adding 4%:
//   card = cash / (1 − 0.04) = cash / 0.96
// This 4% is the universal default for all locations. Per-location overrides
// can be stored in locations.cash_discount_pct and flow via `cashDiscountPct`.
//
// MONEY REPRESENTATION (audit #13): order/item/tender money is `numeric(10,2)`
// dollars — EXACT in Postgres, NOT integer cents. Only newer ledger tables
// (payment_transactions, operating_expenses, *_cents columns) use integer cents.
// All gross-up/discount goes through the helpers in this file; JS math always
// rounds with Math.round(x*100)/100 to stay cent-exact.
export const CASH_DISCOUNT_PCT = 0.04

// ── Impuesto sobre ventas: encima o incluido ─────────────────────────────────
// Plan: docs/superpowers/plans/2026-09-18-precios-con-impuesto-incluido.md
//
// UNA sola función para los dos modos del local (locations.prices_include_tax).
// Todos los motores que calculan impuesto pasan por acá — POS, cierre de tab,
// online, catering, pre-cuenta — para que cliente y servidor no puedan divergir.
//
//   encima (default, todos los locales hasta hoy):
//     base = monto; impuesto = round(base × tasa); el total SUMA el impuesto.
//     Byte a byte la expresión que cada motor usaba antes.
//   incluido (cartelera "TAX INCLUIDO"):
//     el monto YA trae el impuesto. bruto = round(monto) a centavo;
//     base = round(bruto / (1 + tasa)); impuesto = bruto − base (el RESTO, no un
//     segundo redondeo: base + impuesto == bruto al centavo). El total no suma.
//
// `taxableBase` es SIEMPRE la base sin impuesto — lo que va a
// orders.taxable_base y lo que se declara, en los dos modos. `taxOnTop` es lo
// que el total le suma: el impuesto en modo encima, 0 en incluido.
export type SalesTax = {
  taxableBase: number
  taxAmount: number
  taxOnTop: number
}

export function salesTax(taxableAmount: number, taxRate: number, pricesIncludeTax: boolean = false): SalesTax {
  if (!pricesIncludeTax) {
    const taxAmount = Math.round(taxableAmount * taxRate * 100) / 100
    return { taxableBase: taxableAmount, taxAmount, taxOnTop: taxAmount }
  }
  const grossCents = Math.round(taxableAmount * 100)
  if (!(grossCents > 0) || !(taxRate > 0)) {
    return { taxableBase: Math.max(0, grossCents) / 100, taxAmount: 0, taxOnTop: 0 }
  }
  // Para 8.25 % no hay empate posible (bruto×10000/10825 nunca cae en ,5 ¢);
  // para otras tasas, Math.round es el mismo half-up que usa el resto del motor.
  const baseCents = Math.round(grossCents / (1 + taxRate))
  return { taxableBase: baseCents / 100, taxAmount: (grossCents - baseCents) / 100, taxOnTop: 0 }
}

// North cobra ~$0.10 por cada solicitud de autorización (ventas, reembolsos,
// anulaciones y rechazos). Constante hasta que un local tenga otra tarifa
// (columna futura north_per_auth_fee_cents). Spec 2026-09-27 ventas canónicas §3.
export const NORTH_PER_AUTH_FEE_CENTS = 10

export function calculateOrderTotals(
  items: CartItem[],
  taxRate: number,
  cashDiscountPct: number,
  paymentMethod: "cash" | "card" | "split",
  tipAmount: number = 0,
  manualDiscount: ManualDiscount | null = null,
  // PHASE E: per-transaction flat fee added on top of cashDiscountPct so the
  // cash discount on the customer matches North's per-card-tx cost on the
  // merchant. Defaults to 0 for backward compat — locations that haven't
  // configured it behave as before.
  cashPerTransactionFee: number = 0,
  // PHASE F: when true, the per-transaction flat fee is charged to the
  // CARD/SPLIT customer as an explicit `processingFee` line (and removed
  // from the cash-payer discount, since the cash customer isn't paying the
  // fee either). Net effect: merchant breaks even on the flat fee for every
  // payment method, instead of absorbing it on card.
  processingFeeEnabled: boolean = false,
  // OPEN-TAB / "Cobrar después": subtotal of items ALREADY on the order
  // (sent to KDS in a prior round) that this checkout is settling. `items`
  // above holds only the NEW items added this round — the cart is emptied
  // when a tab is loaded (see useCart.enterOpenTabMode), so without this the
  // checkout total collapses to 0. Folded into rawSubtotal so discounts/tax/
  // cash-discount apply to the combined amount, matching the server recompute
  // in /api/orders/tab/[orderId]/checkout (which sums ALL order_items). Treated
  // as fully taxable — the existing items' per-line is_taxable isn't carried on
  // the client; the server stays authoritative for the booked total.
  existingSubtotal: number = 0,
  // CUPONES (PR-5): matemática del cupón YA validado por /api/coupons/apply.
  // Va ÚLTIMO y con default null para no tocar a ningún llamador existente —
  // sin cupón la función devuelve exactamente los mismos números que antes.
  // El server es la autoridad: esto es el espejo del cliente para que el carrito,
  // el `amountDue` del split y lo que se transmite a la pantalla coincidan con
  // lo que /api/orders va a cobrar.
  coupon: CouponMath | null = null,
  // PRECIOS CON IMPUESTO INCLUIDO (locations.prices_include_tax, vía
  // PricingConfig.pricesIncludeTax). Último y con default false por la misma
  // razón que el cupón: sin él, cada número sale idéntico al de siempre. Con él,
  // el impuesto se EXTRAE del bruto gravable (ver salesTax) y el total no lo suma.
  pricesIncludeTax: boolean = false,
  // A1b (TABC): líneas que el CUPÓN no toca (alcohol), por cart_id — ver
  // couponExclusionsFor (lib/coupons/alcoholExclusion.ts). Último y con default
  // null como los dos de arriba: null, un Set vacío o ids que no están en el
  // carrito dan EXACTAMENTE el objeto de main (mismas claves, mismos números).
  discountExclusions: CouponDiscountExclusions | null = null
): OrderTotals & { discountAmount: number; couponDiscount: number; manualDiscountAmount: number; pricesIncludeTax: boolean; couponExcludedAlcohol?: true } {
  // rawSubtotal sums card prices (items.price + modifier price_deltas), plus
  // any existingSubtotal carried from a loaded open tab. Under Phase D
  // semantics items.price IS the card/regular price.
  const rawSubtotal = items.reduce((sum, item) => sum + cartLineRawTotal(item), existingSubtotal)

  // Q1 / B3: per-item tax exemption. Compute the taxable share of the
  // subtotal so tax applies only to items where is_taxable !== false. The
  // manual discount and cash discount get pro-rated by the same ratio so a
  // non-taxable item still benefits from a cash discount but escapes tax.
  // existingSubtotal (open-tab carry-over) is treated as taxable here.
  const taxableRawSubtotal = items.reduce(
    (sum, item) => (item.is_taxable === false ? sum : sum + cartLineRawTotal(item)),
    existingSubtotal
  )
  const taxableRatio = rawSubtotal > 0 ? taxableRawSubtotal / rawSubtotal : 1

  // Stack de descuentos (cupones PR-1, spec §4). MISMA función que el server
  // corre en app/api/orders/route.ts — cliente y servidor son espejo por
  // CONSTRUCCIÓN, no por dos copias de la aritmética que alguien tiene que
  // mantener sincronizadas a mano. Si el número del carrito no coincide con el
  // del server, el split se cae (`Math.abs(tenderSum - total) > 0.02`).
  //
  // Cuando la suma se pasa del subtotal el tope muerde con ASIMETRÍA: un manual
  // 'supervisor' se absorbe y el cupón conserva su matemática validada; un
  // manual 'loyalty' JAMÁS se recorta (el cliente ya lo pagó con puntos) y cede
  // el cupón (backstop P0-16). `source` ausente = 'supervisor', que es lo que
  // aplica el gate de PIN de hoy.
  //
  // SIN cupón el resultado es byte-idéntico al cálculo anterior: couponAmount 0
  // ⇒ la suma nunca supera rawSubtotal ⇒ manualAmount es exactamente el viejo
  // discountAmount (misma secuencia computar → topar en rawSubtotal → redondear
  // a centavo) y totalDiscount === manualAmount.
  // PR-2a — ESPEJO POS, riel PRE-cobro que corre en el NAVEGADOR. `CouponMath`
  // prohibe el ambito por TIPOS (`scope?: never`, ver lib/coupons/engine.ts),
  // pero el tipo NO ve un `any` ni un spread del snapshot completo, que es justo
  // como el ambito se cuela. Si se cuela, `applyDiscountStack` descontaria sobre
  // el subtotal COMPLETO y el carrito le prometeria al cliente un numero que el
  // cobro no va a respetar (y el split se caeria: |tenderSum − total| > 0.02).
  //
  // Rechazar aca es seguro: no se movio dinero. "Rechazar" en una funcion de
  // totales = no pintar el descuento, o sea tratar el cupon como ausente.
  //
  // `snapshotHasScope` es LAXO (`!= null`) precisamente para poder servir a este
  // riel: aca el objeto llega sin esas tres claves en el 100% del trafico de hoy.
  // Ver su docstring en lib/coupons/scopeTripwire.ts.
  // `!= null` (laxo), como el resto del PR: `coupon` es seguro de leer con
  // `undefined` sin depender del default del parametro.
  const couponHasScope = coupon != null && snapshotHasScope(coupon)
  const stackCoupon = couponHasScope ? null : coupon

  // A1b (TABC): el cupón se computa sobre la base SIN alcohol — espejo de
  // /api/orders, que arma la misma base con couponBaseFromLines sobre el
  // rawSubtotal redondeado a centavo y el aporte de cada línea con la misma
  // expresión (cartLineRawTotal ⇔ (precio + mods) × cantidad / retailLineTotal).
  // Solo se mira con cupón Y alguna línea marcada: sin eso no se recorre nada y
  // applyDiscountStack recibe la llamada de main, sin la clave `couponBase`.
  // couponBaseFromLines tiene su camino rápido: ids ajenos ⇒ excluded false.
  // El manual, el tope y el impuesto siguen sobre rawSubtotal (el alcohol sí
  // cuenta para ellos).
  let couponBase: number | undefined
  if (stackCoupon != null && discountExclusions != null && discountExclusions.couponCartIds.size > 0) {
    const base = couponBaseFromLines(
      Math.round(rawSubtotal * 100) / 100,
      items.map((item) => ({
        lineSubtotal: cartLineRawTotal(item),
        isAlcohol: discountExclusions.couponCartIds.has(item.cart_id),
      })),
    )
    if (base.excluded) couponBase = base.couponBase
  }

  const discountStack = applyDiscountStack({
    rawSubtotal,
    ...(couponBase !== undefined ? { couponBase } : {}),
    coupon: stackCoupon,
    manual: manualDiscount
      ? { type: manualDiscount.type, value: manualDiscount.value, source: manualDiscount.source ?? "supervisor" }
      : null,
  })
  const discountAmount = discountStack.totalDiscount
  const couponDiscount = discountStack.couponAmount
  const manualDiscountAmount = discountStack.manualAmount

  const subtotal = rawSubtotal - discountAmount

  // PHASE D: cash discount = explicit discount only when paymentMethod==='cash'.
  // Computed off the post-manual-discount subtotal so multiple discounts stack
  // multiplicatively (consistent with prior behavior).
  // PHASE E: add the per-transaction flat fee. Total cash discount =
  // subtotal × cashDiscountPct + cashPerTransactionFee. This matches what
  // North charges the merchant per card transaction so the merchant breaks
  // even when the customer chooses cash.
  // PHASE F: when `processingFeeEnabled` is on the flat fee no longer needs to
  // ride in the cash discount — the card customer pays it explicitly via
  // `processingFee` instead, so the cash customer doesn't owe a "matching"
  // discount for a fee no one else absorbs.
  //
  // C-12 (audit 2026-05-27): cap cashDiscount at subtotal so tiny orders
  // (e.g. $1.50 with $0.20 flat fee) don't produce negative downstream values.
  const cashFlatFeePortion = processingFeeEnabled ? 0 : cashPerTransactionFee
  const rawCashDiscount = paymentMethod === "cash"
    ? Math.round((subtotal * cashDiscountPct + cashFlatFeePortion) * 100) / 100
    : 0
  // MON-1 (audit 2026-05-29): never let the cash discount swallow the whole
  // subtotal. On tiny tickets the % + flat fee can equal or exceed the
  // subtotal, which — after the C-12 min-clamp at `subtotal` — left the cash
  // customer paying $0.00 for real goods (the merchant giving the item away).
  // Floor the cash-paid goods at 1¢ by capping the discount at subtotal − 0.01,
  // so the cash price stays > 0 and ≤ the card price (the subtotal).
  const maxCashDiscount = subtotal > 0
    ? Math.max(0, Math.round((subtotal - 0.01) * 100) / 100)
    : 0
  const cashDiscount = Math.min(rawCashDiscount, maxCashDiscount)

  // PHASE F: explicit processing fee. Charged to card/split payers when the
  // feature is enabled; never charged on cash. Sits outside subtotal so it
  // doesn't get taxed and renders as its own receipt line.
  const processingFee =
    processingFeeEnabled && (paymentMethod === "card" || paymentMethod === "split")
      ? Math.round(cashPerTransactionFee * 100) / 100
      : 0

  // Tax base = (subtotal − cashDiscount) × taxableRatio.
  // C-12: clamp at zero so a flat-fee-driven cashDiscount > subtotal can't
  // produce negative tax_amount (which would silently undercollect tax and
  // skew fiscal reports).
  // Con impuesto incluido este monto es el BRUTO gravable (trae el impuesto
  // adentro) y salesTax devuelve la base neta; con impuesto encima, la base es
  // este mismo monto — idéntico a antes.
  const taxableGross = Math.max(
    0,
    Math.round((subtotal - cashDiscount) * taxableRatio * 100) / 100
  )
  const tax = salesTax(taxableGross, taxRate, pricesIncludeTax)
  const taxableAmount = tax.taxableBase
  const taxAmount = tax.taxAmount

  // PHASE D: no card surcharge anymore. Card customers pay items.price as-is.
  const cardSurcharge = 0
  // C-12: total clamps at zero. A tip-only "order" with full discount should
  // never bill negative.
  // PHASE F: processingFee adds on top — outside taxable base, so the customer
  // pays it but isn't taxed on it.
  // taxOnTop: el impuesto con impuesto encima; 0 cuando ya viene en el precio.
  const total = Math.max(
    0,
    Math.round((subtotal - cashDiscount + tax.taxOnTop + tipAmount + processingFee) * 100) / 100
  )

  // Backward-compat fields (names kept; semantics updated):
  // - regularSubtotal: was the "displayed card price"; now equals subtotal
  //   (items.price already IS the card price).
  // - cashSavings: actual discount applied when cash; 0 for card/split.
  const regularSubtotal = subtotal
  const cashSavings = cashDiscount

  // discountAmount = manual + cupón (el cash discount sigue viajando aparte en
  // cashSavings, igual que hoy). couponDiscount/manualDiscountAmount son las
  // cubetas YA topadas: la UI debe mostrar ESTAS y no el monto que devolvió
  // /api/coupons/apply, porque cuando el tope muerde una de las dos se recortó.
  //
  // couponExcludedAlcohol (A1b): SOLO cuando el cupón dejó fuera alguna línea de
  // alcohol — es la bandera con la que la UI dice «Cupón no aplica a productos
  // con alcohol». Ausente en todo lo demás, para que el objeto sea el de main.
  return { subtotal, cardSurcharge, taxableAmount, taxAmount, tipAmount, total, cashSavings, regularSubtotal, processingFee, discountAmount, couponDiscount, manualDiscountAmount, pricesIncludeTax, ...(couponBase !== undefined ? { couponExcludedAlcohol: true as const } : {}) }
}

// Impuesto de una ENMIENDA a una orden ya pagada (agregar/quitar ítems): el
// ratio efectivo tax/subtotal de la orden original, como amend-batch (A11).
// Encima: se cobra delta + impuesto (dos redondeos, impuesto primero — byte a
// byte con amend-batch, Cart y POSScreen). Incluido: el delta ya trae su
// impuesto; se cobra tal cual y `taxDelta` es la parte que se suma a
// orders.tax_amount. Mismo ratio en los dos modos: el tax_amount persistido es
// siempre el impuesto real, encima o extraído.
export function amendmentTaxDelta(
  delta: number,
  orderTaxAmount: number,
  orderSubtotal: number,
  pricesIncludeTax: boolean = false,
): { taxDelta: number; charged: number } {
  const ratio = orderSubtotal > 0 ? orderTaxAmount / orderSubtotal : 0
  const taxDelta = Math.round(delta * ratio * 100) / 100
  const charged = Math.round((delta + (pricesIncludeTax ? 0 : taxDelta)) * 100) / 100
  return { taxDelta, charged }
}

// Subtotal SIN impuesto de una orden persistida, a base tarjeta — para los
// reportes que cuentan `orders.subtotal` como ventas. Con impuesto encima es el
// subtotal tal cual. Con impuesto incluido el subtotal trae el impuesto adentro:
// el impuesto salió de lo que se cobró por los bienes (subtotal − descuento de
// efectivo), así que se quita esa misma proporción del subtotal. Tarjeta:
// subtotal − impuesto. Efectivo: se lleva el neto de vuelta a base tarjeta para
// que el `× (1 − pct)` de los reportes siga dando lo cobrado sin impuesto.
export function orderPreTaxSubtotal(o: {
  subtotal: number | null
  tax_amount?: number | null
  cash_savings?: number | null
  prices_include_tax?: boolean | null
}): number {
  const subtotal = Number(o.subtotal ?? 0)
  if (o.prices_include_tax !== true) return subtotal
  const tax = Number(o.tax_amount ?? 0)
  const goodsPaid = subtotal - Number(o.cash_savings ?? 0)
  if (!(goodsPaid > 0) || !(tax > 0)) return subtotal
  return Math.round(subtotal * ((goodsPaid - tax) / goodsPaid) * 100) / 100
}

// Fracción del subtotal de una orden que NO es impuesto — para prorratear a las
// LÍNEAS de esa orden lo que orderPreTaxSubtotal le quita al total. Lo usa el
// P&L, que suma ventas netas línea por línea (unit_price × qty) en vez de por
// orden: sin esto, con impuesto incluido cada línea contaría su impuesto como
// venta y el food cost % saldría bajo.
//
// Devuelve EXACTAMENTE 1 con impuesto encima (y también si el subtotal es 0 o
// negativo, donde no hay nada que prorratear), así que `x * share === x`: cada
// número queda byte a byte igual al de hoy mientras el flag esté apagado.
export function orderPreTaxShare(o: {
  subtotal: number | null
  tax_amount?: number | null
  cash_savings?: number | null
  prices_include_tax?: boolean | null
}): number {
  if (o.prices_include_tax !== true) return 1
  const subtotal = Number(o.subtotal ?? 0)
  if (!(subtotal > 0)) return 1
  return orderPreTaxSubtotal(o) / subtotal
}

// Total de UNA línea del carrito con los precios tal como vienen en el CartItem
// (ya efectivos si pasaron por effectiveCartItems). Por unidad: (precio + mods) ×
// cantidad, sin redondear — byte a byte lo de siempre. Por PESO (retail,
// weight != null): retailLineTotal, la MISMA función con la que /api/orders
// cobra y guarda la línea (precio por unidad de peso × peso, redondeado a
// centavo; ni la cantidad ni los modificadores entran). Antes el carrito sumaba
// precio × cantidad también para el peso: 2,5 lb a $3,99/lb se cobraba $3,99 en
// el terminal mientras el servidor guardaba $9,98.
function cartLineRawTotal(item: CartItem): number {
  const modifiersTotal = item.modifiers.reduce((s, m) => s + m.price_delta * (m.quantity ?? 1), 0)
  if (item.weight != null) {
    return retailLineTotal({ unitPrice: item.price, modifiersTotal, quantity: item.quantity, weight: item.weight })
  }
  return (item.price + modifiersTotal) * item.quantity
}

// Precio efectivo por unidad de venta de la BASE de una línea. El redondeo de
// tarjeta al $0.50 es por unidad de venta: no aplica al precio por libra/kilo de
// una línea por peso (paridad con reRoundSnapshotLine y con /api/orders).
function effectiveLineBasePrice(item: CartItem, cfg: PricingConfig, roundCard: boolean): number {
  return roundCard && item.weight == null ? effectiveItemCardPrice(item.price, cfg) : effectiveUnitPrice(item.price, cfg)
}

export function cartItemLineTotal(item: CartItem, cfg?: PricingConfig): number {
  if (!cfg) return cartLineRawTotal(item)
  return cartLineRawTotal({
    ...item,
    price: effectiveLineBasePrice(item, cfg, true),
    modifiers: item.modifiers.map((m) => ({ ...m, price_delta: effectiveUnitPrice(m.price_delta, cfg) })),
  })
}

// Card price to DISPLAY for a menu item on customer-facing surfaces (kiosk,
// microsite, display board). Under the cash-first model items.price is ALREADY
// the card price, so these surfaces must NOT gross it up again with ÷(1−pct):
// doing so over-states the price vs what the POS/charge path actually bills.
// MON-2 (audit 2026-05-29): kiosk surfaces were re-grossing; this is the shared
// source of truth. `modifierDeltas` are the selected add-on price_deltas
// (already card-priced); pass 0 for a base price.
export function menuItemCardPrice(price: number, modifierDeltas: number = 0): number {
  return Math.round((price + modifierDeltas) * 100) / 100
}

// Propina NETA que recibe el EMPLEADO. Fuente ÚNICA para todo lo que se PAGA o
// acredita al empleado (reporte de Equipo, Nómina, Z/X-report por cajero) — NO
// para lo que se cobra al cliente ni para reportes de ventas/dashboard (ahí la
// propina es BRUTA, es dinero que se movió).
//
// El negocio absorbe el spread del dual pricing (cashDiscountPct) SOLO cuando la
// propina se cobra por tarjeta: el procesador cobra su fee sobre todo el monto
// cargado, propina incluida. En efectivo no hay procesador → el empleado recibe
// el 100%. Split se trata como tarjeta (la propina se carga al lado electrónico).
// Redondea a centavo (cada propina es un monto de dinero real).
export function netEmployeeTip(
  tipAmount: number | null,
  paymentMethod: string | null,
  cashDiscountPct: number
): number {
  const tip = tipAmount ?? 0
  if (tip === 0) return 0
  if (paymentMethod === "cash") return Math.round(tip * 100) / 100
  return Math.round(tip * (1 - cashDiscountPct) * 100) / 100
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

// Round UP to the nearest "menu-friendly" price ending so the merchant never loses money.
// Allowed endings (cents): .00 .09 .19 .25 .29 .39 .49 .50 .59 .69 .75 .79 .89 .95 .99
const NICE_ENDINGS = [0, 9, 19, 25, 29, 39, 49, 50, 59, 69, 75, 79, 89, 95, 99]

export function menuRoundUp(price: number): number {
  const cents = Math.round(price * 100)
  const dollars = Math.floor(cents / 100)
  const remainder = cents % 100
  const ending = NICE_ENDINGS.find(e => e >= remainder)
  if (ending !== undefined) return (dollars * 100 + ending) / 100
  // remainder > 99 (floating point edge) — go to next dollar
  return (dollars + 1)
}

// Redondea precio de TARJETA al $0.50 más cercano, nunca por debajo del exacto
// (piso anti-inversión). Cents enteros → ≥ exacto por construcción.
// Asume input cuantizado a centavo (numeric(10,2)); sub-centavos (p.ej. 0.001)
// caen a 0 por el guard.
export function roundCardTo50(exactCard: number): number {
  if (!(exactCard > 0)) return 0
  const cents = Math.round(exactCard * 100)
  let nearest50 = Math.round(cents / 50) * 50
  if (nearest50 < cents) nearest50 += 50
  return nearest50 / 100
}

// Dual pricing helpers used by the menu builder.
//
// SYSTEM NORM: The merchant enters the CASH price (what they want to receive).
// The system derives the card (menu) price automatically using 4%:
//   card = cash / (1 − 0.04) = cash / 0.96
//
// Example: merchant wants $10.00 cash
//   card = 10.00 / 0.96 = $10.42
//   Cash customer pays:  $10.42 × 0.96 = $10.00 ✓
//   Card customer pays:  $10.42 (processor fee absorbed in the spread)
//
// The pct argument allows per-location overrides; the system default is 0.04.
export function netToCard(net: number, pct: number): number {
  if (net <= 0) return 0
  if (pct >= 1) return 0
  const card = net / (1 - pct)
  return Math.round(card * 100) / 100
}

// Cash price the customer actually pays when they choose cash. Mirrors the
// runtime cash-discount math in calculateOrderTotals() (Phase F): the cash
// discount is ONLY the processor percentage. The per-transaction flat fee
// only applies on card payments (there's no processor on cash) so the
// customer doesn't get that $0.20 off.
//
// `flatFee` arg kept in the signature for API stability; it has no effect.
export function cardToCash(card: number, pct: number, flatFee: number = 0): number {
  if (card <= 0) return 0
  void flatFee
  const cash = card * (1 - pct)
  return Math.round(cash * 100) / 100
}

// Inverse of netToCard for the per-item view. Returns the IDEAL net (i.e. what
// the merchant gets when ignoring the per-order flat fee). The actual realized
// net depends on order size — small tickets lose more to the flat fee.
export function cardToNet(card: number, pct: number): number {
  if (card <= 0) return 0
  const net = card * (1 - pct)
  return Math.round(net * 100) / 100
}

// Ingreso NETO del comerciante por UN ítem del menú, a partir de items.price
// (precio CARD). Es el cardToNet de siempre cuando el impuesto va encima; con
// precios con impuesto incluido ese precio de cartelera TRAE el impuesto, así
// que primero se extrae (salesTax, la única función de extracción del repo) y
// el neto se calcula sobre la base.
//
// Es una aproximación POR ÍTEM: al cobrar, el impuesto se extrae del bruto
// gravable de la orden ENTERA, no ítem por ítem, así que la suma de estos netos
// puede diferir del neto de la orden en centavos de redondeo. Sirve para lo que
// la usan sus dos llamadores —margen e ingeniería de menú, que comparan ítems
// entre sí— no para cuadrar dinero cobrado (eso es orderPreTaxSubtotal).
//
// Con el flag apagado devuelve LITERALMENTE cardToNet(card, pct): mismos
// números que hoy, byte a byte.
export function menuPriceToNet(
  cardPrice: number,
  pct: number,
  pricesIncludeTax: boolean = false,
  taxRate: number = 0,
): number {
  if (!pricesIncludeTax) return cardToNet(cardPrice, pct)
  return cardToNet(salesTax(cardPrice, taxRate, true).taxableBase, pct)
}

// ── Single-price toggle (2026-05-29) ──────────────────────────────────────
// PricingConfig is the per-location pricing mode. Dual pricing is the norm
// (default). Single-price mode is a manual exception set in super admin.
export type PricingConfig = {
  dualPricingEnabled: boolean
  singlePriceBasis: "cash" | "card"
  cashDiscountPct: number
  roundCardPrices: boolean
  // Precios con impuesto incluido (locations.prices_include_tax). No cambia qué
  // precio se muestra — items.price sigue siendo el de tarjeta — sino cómo se
  // aplica el impuesto al cobrar (ver salesTax). Opcional: ausente = impuesto
  // encima, así un literal armado a mano nunca lo enciende por accidente.
  pricesIncludeTax?: boolean
}

// Effective unit price to DISPLAY and CHARGE for an item (or a modifier delta).
// `storedCardPrice` is items.price / modifier price_delta (already card-priced).
// Dual mode → identity (zero regression). Single+card → identity. Single+cash →
// the cash price the merchant wants to receive.
export function effectiveUnitPrice(storedCardPrice: number, cfg: PricingConfig): number {
  if (cfg.dualPricingEnabled) return storedCardPrice
  if (cfg.singlePriceBasis === "card") return storedCardPrice
  return cardToCash(storedCardPrice, cfg.cashDiscountPct)
}

// Precio efectivo de un ÍTEM BASE (no modificador) para DISPLAY del titular de tarjeta.
export function effectiveItemCardPrice(storedCardPrice: number, cfg: PricingConfig): number {
  const base = effectiveUnitPrice(storedCardPrice, cfg)
  const isCardPrice = cfg.dualPricingEnabled || cfg.singlePriceBasis === "card"
  return cfg.roundCardPrices && isCardPrice ? roundCardTo50(base) : base
}

// True cuando, para esta config, el lado TARJETA redondea el precio BASE de cada
// ítem al $0.50. Mismo predicado que effectiveItemCardPrice: solo aplica cuando el
// precio almacenado ES un precio de tarjeta (dual, o single con basis 'card'); en
// single+cash la base es un precio de efectivo y NO se redondea.
export function cardRoundingActive(cfg: PricingConfig): boolean {
  const isCardPrice = cfg.dualPricingEnabled || cfg.singlePriceBasis === "card"
  return cfg.roundCardPrices && isCardPrice
}

// Re-redondea una fila de snapshot `order_items` ya bookeada para el lado TARJETA.
// Redondea SOLO la base (`unit_price`) al $0.50; los modificadores (`modifiers_total`,
// per-unit) quedan exactos; `line_total` se recomputa con la fórmula canónica
// (unit + mods) * qty (paridad con retailLineTotal). Ítems por PESO (weight != null)
// no se redondean (el $0.50 por unidad no aplica a precio×peso). Cuando el redondeo
// no está activo, devuelve la base intacta recomputando el line_total canónico.
//
// ÚNICA fuente de la fórmula: la usa el tab-checkout (total in-memory) y la ruta de
// confirmación (persistir snapshot) para que `order.total == Σ line_total` reconcilie.
export function reRoundSnapshotLine(
  row: { unit_price: number | null; modifiers_total: number | null; quantity: number | null; weight?: number | null },
  cfg: PricingConfig,
): { unit_price: number; line_total: number } {
  const unit = Number(row.unit_price ?? 0)
  const mods = Number(row.modifiers_total ?? 0)
  const qty = Number(row.quantity ?? 0)
  if (row.weight != null) {
    return { unit_price: unit, line_total: Math.round(unit * Number(row.weight) * 100) / 100 }
  }
  const roundedUnit = cardRoundingActive(cfg) ? roundCardTo50(unit) : unit
  return { unit_price: roundedUnit, line_total: Math.round((roundedUnit + mods) * qty * 100) / 100 }
}

// Línea de una CUENTA ABIERTA como la ve el titular de TARJETA (display + cobro card).
// El DTO del cliente (openTabMode.existingItems) no trae modifiers_total, así que en vez
// de la fórmula canónica (unit+mods)*qty usamos su forma delta equivalente:
//   line_total + (roundCardTo50(unit) − unit) × qty
// (algebraicamente idéntica porque line_total ya es (unit+mods)*qty redondeado a centavo
// y el delta de base es un número exacto de centavos × qty entero). Ítems por PESO
// (weight != null) quedan exactos — paridad con reRoundSnapshotLine del server. Con el
// redondeo inactivo devuelve line_total intacto.
export function roundedOpenTabLineTotal(
  row: { unit_price: number | null; quantity: number | null; line_total: number | null; weight?: number | null },
  cfg: PricingConfig,
): number {
  const line = Number(row.line_total ?? 0)
  if (!cardRoundingActive(cfg) || row.weight != null) return line
  const unitCents = Math.round(Number(row.unit_price ?? 0) * 100)
  const roundedCents = Math.round(roundCardTo50(Number(row.unit_price ?? 0)) * 100)
  const qty = Number(row.quantity ?? 0)
  return Math.round(line * 100 + (roundedCents - unitCents) * qty) / 100
}

// Subtotal EXISTENTE de una cuenta abierta para el COBRO (payment-aware). Es el gemelo
// cliente del Σ reRoundSnapshotLine del server en /api/orders/tab/[orderId]/checkout:
// TARJETA con redondeo activo → Σ línea re-redondeada (excluyendo comped, igual que
// existingSubtotal); efectivo/split/otros o redondeo inactivo → el subtotal exacto tal
// cual (byte-idéntico a hoy). Sin este helper el display del checkout de tabs mostraba
// el total exacto mientras el terminal cobraba el redondeado (coca 2.08: pantalla $2.25
// vs cobro $2.71).
export function effectiveOpenTabSubtotal(
  rows: Array<{ unit_price: number | null; quantity: number | null; line_total: number | null; weight?: number | null; is_comped?: boolean }>,
  cfg: PricingConfig,
  paymentMethod: string,
  exactSubtotal: number,
): number {
  if (paymentMethod !== "card" || !cardRoundingActive(cfg)) return exactSubtotal
  if (rows.length === 0) return exactSubtotal
  const sum = rows
    .filter((r) => !r.is_comped)
    .reduce((s, r) => s + Math.round(roundedOpenTabLineTotal(r, cfg) * 100), 0)
  return sum / 100
}

// Transforma el carrito para COBRAR (payment-aware). Redondea base solo si
// cfg.roundCardPrices && paymentMethod==="card" (efectivo/split exactos) y nunca
// en una línea por peso (`weight` viaja intacto en el spread). Modificadores
// siempre effectiveUnitPrice. Fast path dual sin redondeo → misma referencia.
export function effectiveCartItems(items: CartItem[], cfg: PricingConfig, paymentMethod: "cash" | "card" | "split"): CartItem[] {
  const roundsCard = cfg.roundCardPrices && paymentMethod === "card"
  if (cfg.dualPricingEnabled && !roundsCard) return items
  return items.map((i) => ({
    ...i,
    price: effectiveLineBasePrice(i, cfg, roundsCard),
    modifiers: i.modifiers.map((m) => ({ ...m, price_delta: effectiveUnitPrice(m.price_delta, cfg) })),
  }))
}

// Totales del carrito para COBRAR, en UNA sola función pura. La usan el carrito
// (useCartTotals), la pantalla de cobro (useCheckoutTotals) y todo lo que necesite
// "el mismo número que la caja" sin pasar por un hook (p. ej. las etiquetas de
// estante). Es, tal cual y con el orden de argumentos de siempre:
//   calculateOrderTotals(effectiveCartItems(items, cfg, paymentMethod), taxRate,
//     effectiveCashDiscountPct(cfg), paymentMethod, …, cfg.pricesIncludeTax === true)
// Extracción pura: lo cubre lib/utils/computeCartTotals.test.ts contra una copia
// congelada del cálculo anterior.
//
// `existingSubtotal` entra TAL CUAL a calculateOrderTotals: quien lo arma (la cuenta
// abierta de useCheckoutTotals) ya lo pasó por effectiveOpenTabSubtotal →
// effectiveUnitPrice. Ausente = 0, que es lo que useCartTotals siempre pasó.
export function computeCartTotals(p: {
  items: CartItem[]
  taxRate: number
  cfg: PricingConfig
  paymentMethod: "cash" | "card" | "split"
  tipAmount?: number
  manualDiscount?: ManualDiscount | null
  cashPerTransactionFee?: number
  processingFeeEnabled?: boolean
  existingSubtotal?: number
  coupon?: CouponMath | null
  /** A1b: líneas que el cupón no toca (couponExclusionsFor). Ausente/null = main.
   *  Se calculan sobre el carrito CRUDO: effectiveCartItems conserva el cart_id. */
  discountExclusions?: CouponDiscountExclusions | null
}): ReturnType<typeof calculateOrderTotals> {
  const effItems = effectiveCartItems(p.items, p.cfg, p.paymentMethod)
  return calculateOrderTotals(
    effItems,
    p.taxRate,
    effectiveCashDiscountPct(p.cfg),
    p.paymentMethod,
    p.tipAmount,
    p.manualDiscount,
    p.cashPerTransactionFee,
    p.processingFeeEnabled,
    p.existingSubtotal,
    p.coupon,
    p.cfg.pricesIncludeTax === true,
    p.discountExclusions ?? null
  )
}

// Effective cash discount pct fed to the inline charge math. Single-price mode
// has no cash discount (the basis already chose the price), so it returns 0.
export function effectiveCashDiscountPct(cfg: PricingConfig): number {
  return cfg.dualPricingEnabled ? cfg.cashDiscountPct : 0
}

// One source of truth for which location columns the pricing logic needs.
// Interpolate this into every charge-route select so no route can silently
// omit a column (an omitted column → undefined → falls back to DUAL, which
// would make single-price mode quietly not apply on that route).
export const PRICING_SELECT_FIELDS = "dual_pricing_enabled, single_price_basis, cash_discount_pct, round_card_prices"

// Build a PricingConfig from a raw location row. Defensive: a NULL/undefined
// flag falls into dual (the norm), never single-price by accident.
export function pricingConfigFromLocation(location: {
  dual_pricing_enabled?: boolean | null
  single_price_basis?: string | null
  cash_discount_pct?: number | null
  round_card_prices?: boolean | null
  // Ausente cuando el select no la pidió o la migración no corrió todavía
  // (lib/supabase/optionalColumns.ts): cae en impuesto encima, que es la verdad
  // mientras la columna no existe.
  prices_include_tax?: boolean | null
}): PricingConfig {
  // Dev guard for the "forgot to select the columns" bug: if the row is missing
  // the keys ENTIRELY (vs. present-but-null), the select likely omitted them and
  // single-price mode would silently not apply. Warn loudly in dev; prod stays quiet.
  if (process.env.NODE_ENV !== "production") {
    const missing = (["dual_pricing_enabled", "single_price_basis", "round_card_prices"] as const).filter(
      (k) => !(k in location)
    )
    if (missing.length) {
      console.warn(
        `[pricing] location row missing ${missing.join(", ")} — select likely omitted pricing columns; falling back to DUAL`
      )
    }
  }
  return {
    dualPricingEnabled: location.dual_pricing_enabled ?? true,
    singlePriceBasis: location.single_price_basis === "card" ? "card" : "cash",
    cashDiscountPct: location.cash_discount_pct ?? CASH_DISCOUNT_PCT,
    roundCardPrices: location.round_card_prices ?? false,
    pricesIncludeTax: location.prices_include_tax === true,
  }
}

// Suma de modificadores POR UNIDAD de ítem, a partir de un mapa
// option_id → price_delta (ya efectivo, p.ej. effectiveModPriceMap del amend).
// Multiplica cada delta por la cantidad del modificador (CartModifier.quantity;
// undefined/null = 1) — mirror exacto del factor que aplica cartItemLineTotal en
// el camino del cliente. Existe para que el cómputo de dinero del servidor en el
// flujo de ORDER AMENDMENT cuente "2× Carne" igual que el delta que el cliente
// cobró al terminal; sin esto, line_total/order.total quedaban por debajo de lo
// cobrado. Un option_id ausente en el mapa aporta 0 (defensa en profundidad: el
// llamador ya rechazó modificadores desconocidos antes de llegar aquí).
export function modifiersUnitTotalFromMap(
  modifiers: { modifier_option_id: string; quantity?: number | null }[],
  effectivePriceMap: Map<string, number>
): number {
  return modifiers.reduce(
    (s, m) => s + (effectivePriceMap.get(m.modifier_option_id) ?? 0) * (m.quantity ?? 1),
    0
  )
}

// Map id → effective unit price. Drop-in for `new Map(rows.map(r => [r.id, r.price ?? 0]))`.
export function buildEffectivePriceMap(rows: { id: string; price: number | null }[], cfg: PricingConfig, opts?: { cardCharge?: boolean }): Map<string, number> {
  const resolve = opts?.cardCharge ? (p: number) => effectiveItemCardPrice(p, cfg) : (p: number) => effectiveUnitPrice(p, cfg)
  return new Map(rows.map((r) => [r.id, resolve(r.price ?? 0)]))
}

// Dual-pricing conversion for modifier deltas (audit #10 / V4).
//
// SYSTEM NORM (Phase D, see above): the operator types the CASH add-on they want
// to receive; modifier_options.price_delta is STORED in CARD, exactly like items.price.
// Card customers pay the stored delta as-is; cash customers get the spread back via
// the cash discount on the order total.
//
// Unlike item prices (always > 0), a modifier delta CAN be negative (a discount
// option, e.g. "−$2 sin queso"). netToCard/cardToCash clamp non-positive inputs
// to 0, so we gross/ungross the MAGNITUDE and re-apply the sign — a −$2 cash
// discount is stored as −$2.08 card so the card customer gets the equivalent
// removal. 0 stays 0 (grossing 0 = 0).

// ON SAVE: cash delta typed by the operator → CARD value stored in the DB.
export function modifierDeltaToStored(cashDelta: number, pct: number): number {
  if (!cashDelta) return 0
  const sign = cashDelta < 0 ? -1 : 1
  return sign * netToCard(Math.abs(cashDelta), pct)
}

// ON LOAD: CARD delta stored in the DB → cash value shown in the builder input.
export function modifierDeltaToCashInput(storedCard: number, pct: number): number {
  if (!storedCard) return 0
  const sign = storedCard < 0 ? -1 : 1
  return sign * cardToCash(Math.abs(storedCard), pct)
}
